import { useEffect } from 'react';
import * as yup from 'yup';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { CiselnikyVolbyKoloDto } from '@gov-nx/api';
import { useLocale } from '@gov-nx/core/service';
import { equals, pairs, prop } from '@gov-nx/core/types';
import type { ServiceCode } from '@gov-nx/module/service';
import { getSelectedRoundKeys } from '@gov-nx/module/service';
import type { FormCheckboxProps } from '@gov-nx/ui/types';
import { toDateReadable } from '@gov-nx/utils/common';
import type { FormDataStep1 } from './context.types';
import type { IFormStep1 } from './form.types';
import { convertToRoman, electionRoundDefaultValues } from './formDefinitions';

interface FormStep1Props {
	code: ServiceCode;
	rounds: CiselnikyVolbyKoloDto[];
	isProcessing: boolean;
	isDisabled: boolean;
}

export const hasDatesProperties = (
	object: CiselnikyVolbyKoloDto
): object is Required<Pick<CiselnikyVolbyKoloDto, 'terminOd' | 'terminDo'>> &
	CiselnikyVolbyKoloDto => !!(object.terminOd && object.terminDo);

export const FormStep1 = ({
	code,
	rounds,
	isProcessing,
	isDisabled,
}: FormStep1Props): IFormStep1 => {
	const { t } = useLocale(code);

	const checkboxRequiredMessage = t('formular.validace.kolo.povinny');

	const electionRoundFields: Array<
		FormCheckboxProps['field'] & { key: string }
	> = rounds.filter(hasDatesProperties).map((round, index) => {
		const key = `${index + 1}`;
		return {
			key,
			name: `electionRound.${key}`,
			isDisabled:
				!round.lzePodatZadost ||
				round.zadostPodana ||
				isProcessing ||
				isDisabled,
			size: 'm',
			label: {
				children: t('formular.pole.kolo', {
					kolo: convertToRoman(key) ?? '',
					od: toDateReadable(round.terminOd),
					do: toDateReadable(round.terminDo),
				}),
			},
		};
	});

	const keys = electionRoundFields.map(prop('key'));

	const electionRoundSchema = keys.reduce(
		(all, key) => ({
			...all,
			[key]: yup.boolean().optional(),
		}),
		{}
	);

	const isAtleastOneSelected = (fields: Record<string, boolean>) =>
		Object.values(fields).some(equals(true));

	const formSchema = yup
		.object({
			electionRound: yup
				.object()
				.shape(electionRoundSchema, pairs(keys))
				.required(checkboxRequiredMessage)
				.test('at-least-one-selected', checkboxRequiredMessage, (fields) =>
					fields ? isAtleastOneSelected(fields) : false
				),
		})
		.required(checkboxRequiredMessage);

	const electionRoundDefaults = electionRoundDefaultValues(rounds);

	const formMethods = useGovForm<FormDataStep1>({
		formSchema,
		defaultValues: {
			electionRound: electionRoundDefaults,
		},
	});

	useEffect(() => {
		formMethods.reset({ electionRound: electionRoundDefaults });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [keys.length]);

	const electionRound = formMethods.watch('electionRound');
	const isNoRoundSelected = getSelectedRoundKeys(electionRound).length === 0;

	return {
		fields: {
			electionRound: electionRoundFields,
			submit: {
				isLoading: isProcessing,
				isDisabled: isProcessing || isDisabled || isNoRoundSelected,
				label: t('formular.tlacitko.pokracovat'),
				wcagLabel: t('formular.tlacitko.pokracovat'),
			},
		},
		formDefinition: getFormDefinition({ formMethods, formSchema }),
	};
};
